import React from "react";
import { DateRangeProvider } from "../contexts/DateRangeContext";
import { IMerchant } from "../interfaces";
import {
  AcceptInvitation,
  AppCountryReports,
  AppDetail,
  AppList,
  AppSelection,
  AppleAdsCampaignDetail,
  AppleAdsCampaignList,
  AppleAdsHistory,
  Auth,
  BidManagement,
  Billing,
  BillingHistory,
  Campaigns,
  Dashboard,
  ForgotPassword,
  GoogleAdsCampaignDetail,
  GoogleAdsCampaignList,
  GoogleAdsHistory,
  IntegrationResolver,
  Integrations,
  Merchants,
  MetaAdsCampaignDetail,
  MetaAdsCampaignList,
  MetaAdsHistory,
  NotFound,
  OrdersList,
  PaymentVerify,
  Pricing,
  ResetPassword,
  RoleForm,
  RoleManagement,
  SessionActivity,
  Settings,
  Support,
  TakeOverLogin,
  TeamManagement,
  UserActivity,
  WorkspaceSetup
} from "../routes/asyncComponents";

import {
  AndroidLogoIcon,
  ClockUser,
  CreditCard,
  Globe,
  GoogleLogo,
  GooglePlayLogo,
  MegaphoneIcon,
  Shield,
  Sparkle,
  SquaresFour,
  Users
} from "@phosphor-icons/react";
import { Navigate } from "react-router-dom";
import { IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { PERMISSION_MAPPER, PLATFORM_ICON_MAP } from "../shared/constants";

export interface RouteConfig {
  path: string;
  element?: React.ReactNode;
  protected?: boolean;
  isSuperAdminRoute?: boolean;
  permission?: string;
  label?: string;
  icon?: React.ReactNode;
  submenu?: RouteConfig[];
  excludeForCustomAdmin?: boolean;
  breadcrumb?: string | "appName";
  noLayout?: boolean;
}

export const routesConfig: RouteConfig[] = [
  // Public Routes

  {
    path: "/auth",
    element: <Auth />,
    protected: false,
  },
  {
    path: "/accept-invite",
    element: <AcceptInvitation />,
    protected: false,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    protected: false,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    protected: false,
  },
  {
    path: "/accept-invitation",
    element: <AcceptInvitation />,
    protected: false,
  },
  // Protected Routes
  {
    path: "/",
    element: <Navigate to="/dashboard" />,
    protected: true,
    permission: PERMISSION_MAPPER.DASHBOARD_READ,
  },
  {
    path: "/dashboard",
    element: (
      <DateRangeProvider>
        <Dashboard />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.DASHBOARD_READ,
    label: "common.dashboard",
    icon: <SquaresFour size={20} weight="duotone" />,
  },
  {
    path: "/app-country-reports",
    element: (
      <DateRangeProvider>
        <AppCountryReports />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_COUNTRY_REPORTS_READ,
    label: "appCountryWiseReports.title",
    icon: <Globe size={20} weight="duotone" />,
  },
  // {
  //   path: "/reports",
  //   element: (
  //     <DateRangeProvider>
  //       <Reports />
  //     </DateRangeProvider>
  //   ),
  //   protected: true,
  //   permission: PERMISSION_MAPPER.APP_READ,
  //   label: "common.reports",
  //   icon: <ChartBar size={20} weight="duotone" />,
  // },
  {
    path: "/app-country-reports/:slug",
    element: (
      <DateRangeProvider>
        <AppDetail />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_FIND_ONE,
    breadcrumb: "appName",
  },
  {
    path: "/app-country-reports/:app-slug/bid-management",
    element: <BidManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.BID_MANAGEMENT_READ,
    breadcrumb: "appName",
  },
  {
    path: "/apps/:app-slug/bid-management",
    element: <BidManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.BID_MANAGEMENT_READ,
    breadcrumb: "appName",
  },
  {
    path: "/merchants",
    element: <Merchants />,
    protected: true,
    isSuperAdminRoute: true,
    label: "common.merchants",
    icon: <Users size={18} />,
  },
  {
    path: "/apps",
    element: (
      <DateRangeProvider>
        <AppList />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_READ,
    label: "common.apps",
    icon: <AndroidLogoIcon size={20} weight="duotone" />,
  },
  {
    path: "/apps/:slug",
    element: (
      <DateRangeProvider>
        <AppDetail />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_FIND_ONE,
    breadcrumb: "appName",
  },
  {
    path: "/campaigns",
    element: (
      <DateRangeProvider>
        <Campaigns />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.APP_READ,
    label: "common.campaignsManagement",
    icon: <MegaphoneIcon size={18} />,
    submenu: [
      {
        path: "/campaigns/google-ads",
        element: (
          <DateRangeProvider>
            <GoogleAdsCampaignList />
          </DateRangeProvider>
        ),
        label: "Google Ads",
        permission: PERMISSION_MAPPER.APP_READ,
        icon: (
          <img
            src={PLATFORM_ICON_MAP.google_ads}
            alt="Google Ads"
            className="w-4 h-4 object-contain"
          />
        ),
      },
      {
        path: "/campaigns/meta-ads",
        element: (
          <DateRangeProvider>
            <MetaAdsCampaignList />
          </DateRangeProvider>
        ),
        label: "Meta Ads",
        permission: PERMISSION_MAPPER.APP_READ,
        icon: (
          <img
            src={PLATFORM_ICON_MAP.meta_ads}
            alt="Meta Ads"
            className="w-4 h-4 object-contain"
          />
        ),
      },
      {
        path: "/campaigns/apple-search-ads",
        element: (
          <DateRangeProvider>
            <AppleAdsCampaignList />
          </DateRangeProvider>
        ),
        label: "Apple Search Ads",
        permission: PERMISSION_MAPPER.APP_READ,
        icon: (
          <img
            src={PLATFORM_ICON_MAP.apple_search_ads}
            alt="Apple Search Ads"
            className="w-4 h-4 object-contain"
          />
        ),
      },
      {
        path: "/campaigns/meta-ads/:campaign_id",
        element: (
          <DateRangeProvider>
            <MetaAdsCampaignDetail />
          </DateRangeProvider>
        ),
        protected: true,
        permission: PERMISSION_MAPPER.CAMPAIGN_READ,
      },
      {
        path: "/campaigns/meta-ads/:campaign_id/history",
        element: (
          <DateRangeProvider>
            <MetaAdsHistory />
          </DateRangeProvider>
        ),
        protected: true,
        permission: PERMISSION_MAPPER.CAMPAIGN_READ,
      },
      {
        path: "/campaigns/google-ads/:campaign_id",
        element: (
          <DateRangeProvider>
            <GoogleAdsCampaignDetail />
          </DateRangeProvider>
        ),
        protected: true,
        permission: PERMISSION_MAPPER.CAMPAIGN_READ,
      },
      {
        path: "/campaigns/google-ads/:campaign_id/history",
        element: (
          <DateRangeProvider>
            <GoogleAdsHistory />
          </DateRangeProvider>
        ),
        protected: true,
        permission: PERMISSION_MAPPER.CAMPAIGN_READ,
      },
      {
        path: "/campaigns/apple-search-ads/:campaign_id",
        element: (
          <DateRangeProvider>
            <AppleAdsCampaignDetail />
          </DateRangeProvider>
        ),
        protected: true,
        permission: PERMISSION_MAPPER.CAMPAIGN_READ,
      },
      {
        path: "/campaigns/apple-search-ads/:campaign_id/history",
        element: (
          <DateRangeProvider>
            <AppleAdsHistory />
          </DateRangeProvider>
        ),
        protected: true,
        permission: PERMISSION_MAPPER.CAMPAIGN_READ,
      },
    ],
  },
  {
    path: "/orders",
    element: (
      <DateRangeProvider>
        <OrdersList />
      </DateRangeProvider>
    ),
    protected: true,
    permission: PERMISSION_MAPPER.ORDER_READ,
    label: "orders.orderManagement",
    icon: <GooglePlayLogo size={20} weight="duotone" />,
    // submenu: [
    //   {
    //     path: "/orders/google-play",
    //     element: (
    //       <DateRangeProvider>
    //         <OrdersList />
    //       </DateRangeProvider>
    //     ),
    //     label: "Google Play IAP",
    //     icon: (
    //       <img
    //         src={PLATFORM_ICON_MAP.google_iap}
    //         alt="Google Play IAP"
    //         className="w-4 h-4 object-contain"
    //       />
    //     ),
    //   },
    //   {
    //     path: "/orders/apple-iap",
    //     element: (
    //       <DateRangeProvider>
    //         <OrdersList />
    //       </DateRangeProvider>
    //     ),
    //     label: "Apple IAP",
    //     icon: (
    //       <img
    //         src={PLATFORM_ICON_MAP.apple_iap}
    //         alt="Apple IAP"
    //         className="w-4 h-4 object-contain"
    //       />
    //     ),
    //   },
    // ],
  },
  {
    path: "/campaigns",
    element: <Campaigns />,
    protected: true,
    permission: PERMISSION_MAPPER.CAMPAIGN_READ,
    // label: "common.campaignsManagement",
    // icon: <Megaphone size={18} />,
  },
  {
    path: "/plans-and-billing",
    label: "sidebar.plans_and_billing",
    icon: <CreditCard size={20} weight="duotone" />,
    protected: true,
    permission: "",
    submenu: [
      {
        path: "/pricing",
        element: <Pricing />,
        protected: true,
        permission: "",
        label: "pricing.title",
        icon: <Sparkle size={20} weight="duotone" />,
        noLayout: true,
      },
      {
        path: "/workspace-setup",
        element: <WorkspaceSetup />,
        protected: true,
        permission: "",
        label: "workspace.setup",
        icon: <Sparkle size={20} weight="duotone" />,
        noLayout: true,
      },
      {
        path: "/billing",
        element: <Billing />,
        protected: true,
        label: "billing.title",
        permission: "",
        icon: <CreditCard size={20} weight="duotone" />,
      },
      {
        path: "/billing-history",
        element: <BillingHistory />,
        protected: true,
        permission: "",
        label: "sidebar.billing_history",
        icon: <ClockUser size={20} weight="duotone" />,
      },
    ],
  },
  // {
  //   path: "/campaigns/:id",
  //   element: <CampaignDetail />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.CAMPAIGN_FIND_ONE,
  // },
  {
    path: "/profile",
    element: <Settings />,
    protected: true,
    isSuperAdminRoute: true,
    permission: "",
  },
  {
    path: "/support",
    element: <Support />,
    protected: true,
    permission: "",
    isSuperAdminRoute: true,
  },
  {
    path: "/integration-pairs",
    element: <Integrations />,
    protected: true,
    permission: PERMISSION_MAPPER.INTEGRATION_READ,
    label: "common.integrations",
    icon: <GoogleLogo size={20} weight="duotone" />,
  },
  {
    path: "/app-selection",
    element: <AppSelection />,
    protected: true,
    permission: PERMISSION_MAPPER.INTEGRATION_READ,
  },
  // {
  //   path: "/configurations/integrations",
  //   element: <Integrations />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_READ,
  // },

  {
    path: "/integrations",
    element: <IntegrationResolver />,
    protected: true,
    permission: PERMISSION_MAPPER.INTEGRATION_READ,
  },
  // {
  //   path: "/integrations/add",
  //   element: <IntegrationResolver />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_CREATE,
  // },
  // {
  //   path: "/integrations/:trackingId",
  //   element: <IntegrationResolver />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_CREATE,
  // },
  // {
  //   path: "/integrations/update",
  //   element: <IntegrationResolver />,
  //   protected: true,
  //   permission: PERMISSION_MAPPER.INTEGRATION_UPDATE,
  // },
  {
    path: "/team-management",
    element: <TeamManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.TEAM_MANAGEMENT_READ,
    label: "configurations.team.title",
    icon: <Users size={20} weight="duotone" />,
  },
  {
    path: "/role-management",
    element: <RoleManagement />,
    protected: true,
    permission: PERMISSION_MAPPER.ROLE_MANAGEMENT_READ,
    label: "configurations.roles.title",
    icon: <Shield size={20} weight="duotone" />,
    excludeForCustomAdmin: true,
  },
  {
    path: "/role-management/create",
    element: <RoleForm />,
    protected: true,
    permission: PERMISSION_MAPPER.ROLE_MANAGEMENT_CREATE,
    excludeForCustomAdmin: true,
  },
  {
    path: "/role-management/:id",
    element: <RoleForm />,
    protected: true,
    permission: PERMISSION_MAPPER.ROLE_MANAGEMENT_UPDATE,
    excludeForCustomAdmin: true,
  },
  {
    path: "/user-activity",
    element: <UserActivity />,
    protected: true,
    permission: "",
    label: "userActivity.title",
    icon: <ClockUser size={20} weight="duotone" />,
  },
  {
    path: "/sessions-activity",
    element: <SessionActivity />,
    protected: true,
    permission: "SESSION_ACTIVITY_READ",
  },
  // {
  //   path: "/activities",
  //   element: <Ghost />,
  //   protected: true,
  //   permission: "",
  //   label: "common.activities",
  //   icon: <SquaresFour size={20} weight="duotone" />,
  //   submenu: [
  //     {
  //       path: "/activities/user-activity",
  //       element: <UserActivity />,
  //       protected: true,
  //       permission: "",
  //       label: "userActivity.title",
  //       icon: <MapPinSimple size={20} weight="duotone" />,
  //     },
  //     {
  //       path: "/activities/sessions-activity",
  //       element: <SessionActivity />,
  //       protected: true,
  //       permission: "",
  //       label: "configurations.team.sessions.button",
  //       icon: <ClockUser size={20} weight="duotone" />,
  //     },
  //   ],
  // },
  {
    path: "/payment/verify",
    element: <PaymentVerify />,
    protected: true,
    permission: "",
    noLayout: true,
  },
  {
    path: "/take-over-login",
    element: <TakeOverLogin />,
    protected: false,
  },
  // {
  //   path: "/configurations",
  //   element: <Ghost />,
  //   protected: true,
  //   allowedRoles: ["admin", "sub_admin"],
  //   label: "common.configurations",
  //   icon: <SettingsIcon size={18} />,
  //   submenu: [
  //     {
  //       path: "/configurations/integrations",
  //       element: <Integrations />,
  //       protected: true,
  //       allowedRoles: ["admin", "sub_admin"],
  //       label: "common.integrations",
  //       icon: <Puzzle size={18} />,
  //     },
  //     {
  //       path: "/integrations/add",
  //       element: <AddIntegration />,
  //       protected: true,
  //       allowedRoles: ["admin", "sub_admin"],
  //     },
  //     {
  //       path: "/configurations/team-management",
  //       element: <TeamManagement />,
  //       protected: true,
  //       allowedRoles: ["admin"],
  //       label: "configurations.team.title",
  //       icon: <User size={18} />,
  //     },
  //     {
  //       path: "/configurations/role-management",
  //       element: <RoleManagement />,
  //       protected: true,
  //       allowedRoles: ["admin"],
  //       label: "configurations.roles.title",
  //       icon: <Shield size={18} />,
  //     },
  //   ],
  // },
  {
    path: "/404",
    element: <NotFound />,
    protected: false,
  },
];

// Helper functions to get specific route configurations
export const getPublicRoutes = () =>
  routesConfig.filter((route) => !route.protected);

export const flattenRoutes = (routes: RouteConfig[]): RouteConfig[] => {
  return routes.reduce((acc: RouteConfig[], route) => {
    acc.push(route);
    if (route.submenu) {
      acc.push(...flattenRoutes(route.submenu));
    }
    return acc;
  }, []);
};

export const getProtectedRoutes = (isSuperAdmin: boolean) => {
  return flattenRoutes(
    routesConfig.filter((route) =>
      isSuperAdmin
        ? route.protected && route.isSuperAdminRoute
        : route.protected,
    ),
  );
};

export const getNavItems = (user: IMerchant | null, isVip: boolean) => {
  if (!user) return [];

  const permittedRoutes = getPermittedRoutes(user);
  return permittedRoutes
    .filter((route) => {
      if (!route.label || !route.icon) return false;
      // Plans & Billing is only for non-VIP team admins
      if (route.path === '/plans-and-billing' && (!user.is_team_admin || isVip)) return false;
      return true;
    })
    .map((route) => {
      if (route.submenu) {
        const filteredSubmenu = route.submenu.filter((sub) => {
          if (!sub.label || !sub.icon) return false;
          // Pricing and workspace-setup are never shown in the sidebar
          if (sub.path === '/pricing') return false;
          if (sub.path === '/workspace-setup') return false;
          return true;
        });
        // Return a new object — never mutate the shared routesConfig!
        return {
          ...route,
          submenu: filteredSubmenu.length > 0 ? filteredSubmenu : undefined,
        };
      }
      return route;
    });
};

function filterRoutesByPermission(
  routes: RouteConfig[],
  user: IMerchant,
): RouteConfig[] {
  return routes
    .map((route) => {
      let permittedSubmenu: RouteConfig[] | undefined;
      if (route.submenu) {
        permittedSubmenu = filterRoutesByPermission(route.submenu, user);
      }

      // Check permission for this route
      const hasPermission =
        !route.permission ||
        ((user.user_permissions as IPermissionCategories)?.modules || []).some(
          (module) => module.codename === route.permission,
        );

      // Include this route if:
      // - user has permission for this route, or
      // - there are permitted submenus
      if (hasPermission || (permittedSubmenu && permittedSubmenu.length > 0)) {
        // Return a new object — never mutate the shared routesConfig!
        if (permittedSubmenu && permittedSubmenu.length > 0) {
          return { ...route, submenu: permittedSubmenu };
        }
        // Keep original submenu if it exists (no permission filter applied to submenus)
        return { ...route };
      }
      return null;
    })
    .filter(Boolean) as RouteConfig[];
}

export const getPermittedRoutes = (user: IMerchant | null) => {
  if (!user) return [];

  if (user.is_superuser) {
    return routesConfig.filter(
      (route) => route.isSuperAdminRoute && route.protected,
    );
  }

  // custom_admin: full access but excludeForCustomAdmin routes are filtered out.
  // Must be checked before the SCHEMA_LEVEL_ACCESS bypass because normalization
  // sets user_permissions = SCHEMA_LEVEL_ACCESS for custom_admin users.
  if (user.custom_admin) {
    return routesConfig.filter(
      (route) => route.protected && !route.isSuperAdminRoute && !route.excludeForCustomAdmin,
    );
  }

  // Full access bypass for merchant scope
  if (
    user.user_permissions === "*" ||
    user.user_permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
  ) {
    return routesConfig.filter(
      (route) => route.protected && !route.isSuperAdminRoute,
    );
  }

  // Use the recursive filter for normal users and restricted Team Admins
  return filterRoutesByPermission(
    routesConfig.filter((route) => route.protected && !route.isSuperAdminRoute),
    user,
  );
};
